import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { Observable, from, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { AlertService } from "../shared/alert.service";
import { AuthorizationService } from "../shared/authorization.service";
import { SessionService } from "../shared/session.service";
import { CaptchaService } from "../shared/captcha.service";
import { acaoDaRota } from "../shared/captcha-acoes";


@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  ignoreUrls = ['get-ip.feev.com.br'];

  constructor(
    private session: SessionService,
    private alert: AlertService,
    private authorization: AuthorizationService,
    private captcha: CaptchaService
  ) { }

  private blobError(error: HttpErrorResponse) {
    // https://github.com/angular/angular/issues/19888
    // When request of type Blob, the error is also in Blob instead of object of the json data
    return new Promise<any>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: Event) => {
        try {
          const errmsg = (<any>e.target).result;
          reject(new HttpErrorResponse({
            error: errmsg,
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url
          }));

          this.alert.warning(errmsg);
        } catch (e) {
          reject(error);
          this.alert.warning(error.message);
        }
      };

      reader.onerror = (e) => {
        reject(error);
      };

      reader.readAsText(error.error);
    });
  }

  private handleError(req: HttpRequest<any>, error: HttpErrorResponse) {
    if (error.status) {
      if (error.status === 400)
        if (typeof error.error === "string")
          this.alert.warning(error.error);
        else if (error.error instanceof Blob)
          this.blobError(error);
        else {
          if (error.message)
            this.alert.warning(error.message);
          else
            this.alert.warning(JSON.stringify(error.error));
        }
      else
        this.alert.error(`Url: ${req.url}`, error.status);
    }
    else
      this.alert.error(`Url: ${req.url}`);

    return throwError(error);
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse) {
    return this.authorization.renovarToken()
      .pipe(
        switchMap(() => {
          req = this.addAuthHeader(req);
          return next.handle(req);
        })
      );
  }

  private addAuthHeader(request: HttpRequest<any>) {
    for (let x = 0; x < this.ignoreUrls.length; x++) {
      if (request.url.indexOf(this.ignoreUrls[x]) > -1)
        return request;
    }

    if (this.authorization.token) {
      let headers = new HttpHeaders();
      headers = headers.append('Authorization', `Bearer ${this.authorization.token}`);

      if (this.session.usuario.codigoCredor)
        headers = headers.append('CodigoCredor', this.session.usuario.codigoCredor.toString());

      if (this.session.usuario.grupoCredores)
        headers = headers.append('GrupoEmissor', this.session.usuario.grupoCredores);

      return request.clone({
        headers: headers
      });
    }

    return request;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    req = this.addAuthHeader(req);

    // Captcha: só as rotas mapeadas levam token, e só quando há site key configurada. Sem chave,
    // este trecho não faz nada e o fluxo é exatamente o de antes.
    const acao = this.captcha.habilitado ? acaoDaRota(req.url, req.method) : null;

    if (acao) {
      return from(this.captcha.executar(acao)).pipe(
        switchMap(token => this.despachar(token ? this.addCaptchaHeader(req, token) : req, next, true))
      );
    }

    return this.despachar(req, next);
  }

  private addCaptchaHeader(request: HttpRequest<any>, token: string) {
    return request.clone({ setHeaders: { 'X-Captcha-Token': token } });
  }

  /**
   * O backend recusou por captcha (score baixo no v3). Abre o desafio de checkbox e repete a
   * requisição uma única vez — senão um usuário legítimo com score ruim ficaria sem saída.
   * Se o v2 não estiver configurado ou o usuário desistir, o erro segue como veio.
   */
  private tentarDesafio(req: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse): Observable<HttpEvent<any>> {
    return from(this.captcha.desafioV2()).pipe(
      switchMap(token => {
        if (!token) {
          return this.handleError(req, error);
        }

        return this.despachar(this.addCaptchaHeader(req, token), next);
      })
    );
  }

  private ehRecusaDeCaptcha(error: any, podeRepetir: boolean): boolean {
    return podeRepetir
      && error instanceof HttpErrorResponse
      && error.status === 400
      && !!error.headers?.get('X-Captcha-Required')
      && this.captcha.desafioDisponivel;
  }

  private despachar(req: HttpRequest<any>, next: HttpHandler, podeDesafiar = false): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError(
        error => {
          // Vale para /validar e /reenviar: nas duas, 401 quer dizer que a tentativa de acesso
          // caiu do cache, e não que o token da sessão expirou. Não há sessão para renovar aqui —
          // `renovarToken()` devolveria null e o handle401Error estouraria antes de mostrar a
          // mensagem que o backend mandou.
          if (error.status === 401 && req.url.includes('/v2/autenticacao/mfa/')) {
            this.alert.warning(typeof error.error === 'string' && error.error.trim()
              ? error.error
              : 'Não foi possível validar o código. Inicie o acesso novamente.');
            return throwError(error);
          }

          if (error.status === 401)
            return this.handle401Error(req, next, error);

          if (this.ehRecusaDeCaptcha(error, podeDesafiar))
            return this.tentarDesafio(req, next, error);

          return this.handleError(req, error);
        })
    );
  }
}
